import * as React from "react";
import { Check, Copy, Download } from "lucide-react";
import type { BuyerSendVersionDto } from "@/api/buyerSendVersions";
import {
  Button,
  DetailField as Field,
  DetailFieldList,
  Timestamp,
} from "@/design-system/components/ui";
import {
  Blank,
  CarriedInBadge,
  combinedOthersLabel,
  pathLabel,
  versionLabel,
} from "./sendVersionBits";

/**
 * The selected version, rendered — what actually went out, and the three things
 * a specialist can do with it.
 *
 * <p>The three actions are: start a new draft from it, take it away (download or
 * copy), and send that exact version again.</p>
 *
 * <p>🔴 <b>"Send again" is the one that has to be got right.</b> It goes back
 * down the ORDINARY send path with this version's subject and body — it does not
 * write anything itself, and there is no endpoint on the history surface for it
 * to call. That is what keeps `PackageSentAtUtc` on exactly one forward-only
 * writer: the send path stamps <c>??=</c> on the FIRST send of a submission's
 * life and never again, so re-sending appends a version and earns nothing.
 * Pinned server-side by
 * <c>Re_sending_a_package_version_moves_no_entitlement_count</c>. A re-send
 * affordance on a history row is precisely how that invariant gets broken by
 * accident, which is why this component owns no mutation of its own.</p>
 *
 * <p>Actions are gated on `canWrite`, resolved once by the page and passed down.
 * A panel that gates on a row's bare `canEdit` offers writes the server refuses,
 * and the specialist cannot tell that from a broken button.</p>
 */
export function SendVersionDetail({
  version,
  canWrite,
  isLatest,
  onStartDraftFrom,
  onSendAgain,
}: {
  version: BuyerSendVersionDto;
  canWrite: boolean;
  isLatest: boolean;
  onStartDraftFrom?: (version: BuyerSendVersionDto) => void;
  onSendAgain?: (version: BuyerSendVersionDto) => void;
}) {
  const [copied, setCopied] = React.useState(false);

  // Cleared on a timer, and the timer is cleared on unmount — a setState after
  // unmount is a warning in the console and a leak in a long session.
  React.useEffect(() => {
    if (!copied) return;
    const t = window.setTimeout(() => setCopied(false), 2000);
    return () => window.clearTimeout(t);
  }, [copied]);

  // Reset when the reader selects a different version, or the tick stays on
  // beside a body nobody copied.
  React.useEffect(() => setCopied(false), [version.id]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(version.body);
      setCopied(true);
    } catch {
      // A blocked clipboard is not an error worth interrupting anyone over —
      // the body is on screen and selectable, which is the fallback.
    }
  };

  const download = () => {
    const name = `${version.kind === "Package" ? "package" : "intro-email"}-${versionLabel(version)}.txt`;
    const url = URL.createObjectURL(
      new Blob([version.body], { type: "text/plain;charset=utf-8" }),
    );
    const a = document.createElement("a");
    a.href = url;
    a.download = name;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="grid gap-space-3 min-w-0">
      <header className="grid gap-space-2">
        <div className="flex items-center gap-space-2 flex-wrap">
          <h4 className="text-h4 m-0">{versionLabel(version)}</h4>
          {version.recordedBeforeVersioning ? <CarriedInBadge /> : null}
          {isLatest ? (
            <span className="text-caption text-gray-mid">Most recent</span>
          ) : null}
        </div>

        {combinedOthersLabel(version) ? (
          <p className="text-small text-status-info m-0" data-testid="send-version-detail-combined">
            {combinedOthersLabel(version)}. The message below is the whole email the
            buyer received.
          </p>
        ) : null}

        <DetailFieldList>
          <Field label="Sent">
            <Timestamp value={version.sentAtUtc} />
          </Field>
          <Field label="To">{version.toEmail ?? <Blank />}</Field>
          <Field label="By">
            {version.sentByDisplayName ?? version.sentByUserId ?? <Blank />}
          </Field>
          <Field label="Path">
            {version.selfAttested === null ? (
              <span className="text-gray-mid italic">Not recorded</span>
            ) : (
              pathLabel(version)
            )}
          </Field>
          {version.attachmentNames.length > 0 ? (
            <Field label="Attached">{version.attachmentNames.join(", ")}</Field>
          ) : null}
        </DetailFieldList>

        {version.subject ? (
          <p className="text-small m-0">
            <span className="text-gray-mid">Subject: </span>
            <span className="font-medium">{version.subject}</span>
          </p>
        ) : null}
      </header>

      {version.recordedBeforeVersioning ? (
        <p className="text-caption text-gray-mid m-0">
          This send predates the send history, so only its body and its date were
          recorded. The recipient, subject and path were never stored.
        </p>
      ) : null}

      {/* `whitespace-pre-wrap` because the body is plain text composed with real
          line breaks; `break-words` so an unbroken URL cannot widen the pane and
          take the layout's wrap decision with it. */}
      <div className="rounded-card border border-gray-mid/30 bg-white-warm p-space-3">
        <p className="text-small whitespace-pre-wrap break-words m-0">
          {version.body}
        </p>
      </div>

      <div className="flex flex-wrap gap-space-2 items-center">
        <Button variant="secondary" onClick={download}>
          <Download className="h-4 w-4" aria-hidden /> Download
        </Button>

        <Button variant="secondary" onClick={copy}>
          {copied ? (
            <>
              <Check className="h-4 w-4" aria-hidden /> Copied
            </>
          ) : (
            <>
              <Copy className="h-4 w-4" aria-hidden /> Copy
            </>
          )}
        </Button>

        {canWrite && onStartDraftFrom ? (
          <Button variant="text" onClick={() => onStartDraftFrom(version)}>
            Start a new draft from this
          </Button>
        ) : null}

        {canWrite && onSendAgain ? (
          <Button variant="text" onClick={() => onSendAgain(version)}>
            Send this version again
          </Button>
        ) : null}
      </div>
    </div>
  );
}

